<template>
  <span>
    <aha-antd-button
      v-aha-emit-action
      name="reset-results-button"
      :disabled="isResetResultsButtonDisabled"
      variant="secondary"
      @click="isClearDataConfirmationModalShowing = true"
      class="clear-response-bigger"
      iconName="arrow-clock-wise-rtl"
      :iconWidth="16"
      :iconHeight="16"
      v-tooltip="hideTooltip ? '' : tooltipText"
    >
      <span>{{ $i18nPresenter.t('Reset results') }}</span>
    </aha-antd-button>

    <modal-clear-data
      v-if="isClearDataConfirmationModalShowing"
      :title="modalHeading"
      :presentation="presentation"
      :clear-data-handler="clearData"
      @hide="isClearDataConfirmationModalShowing = false"
    />
  </span>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import ModalClearData from '@/components-v2/modals/ClearData.vue'
import Config from '@/config'
import Utils from '@/util'
import constants from '@/constant'
import EventBus from '@/eventBus'
import { getMessagesLocally } from '@/vuex/modules/presentation/chat.module'
import Socket from '@/socket'

const { eventCategories } = constants

export default {
  components: {
    ModalClearData,
  },
  props: {
    hideTooltip: {
      type: Boolean,
      default: false,
    },
    gaAction: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isClearDataConfirmationModalShowing: false,
      loadingClearData: false,
      textReset: this.$i18nPresenter.t('Reset'),
    }
  },

  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapGetters('presentationChat', ['chatMessages']),
    ...mapGetters('multipleSessions', ['canUseMultipleSessions']),
    ...mapState({
      presentation: (state) => state.presentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canEditPresentation: (state) => state.canEditPresentation,
      slideActive: (state) => state.slideActive,
    }),
    modalHeading() {
      return `${this.textReset} '${this.presentation.name}'?`
    },
    isResetResultsButtonDisabled() {
      const chatInLocal = getMessagesLocally(this.presentation.uniqueAccessCode)
      if (chatInLocal.length > 0) {
        return false
      }
      const isDisablePermissionResetResult =
        this.presentation.permissions && this.presentation.permissions.includes(constants.permissions.CLEAR_RESULT)
      if (isDisablePermissionResetResult) {
        return false
      }
      const isHaveChatMessage = this.chatMessages && this.chatMessages
        .filter((chatMess) => !['hasJoined', 'hasLeft'].includes(chatMess.message)).length > 0
      return !isHaveChatMessage > 0 && (!this.canEditPresentation)
    },
    tooltipText() {
      if (this.canUseMultipleSessions) {
        return this.$i18nPresenter.t('Your audience responses are saved in the report. This will just reset the slides for a new session.')
      }
      return this.$i18nPresenter.t('<p>You can delete all the <b>votes</b>, <b>submissions</b> and <b>reactions</b>.</p>')
    },
    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },
  },

  methods: {
    ...mapActions('presentationReport', [
      'clearPresentationReportAction',
    ]),
    ...mapActions(['patchPresentationAction', 'updateSlideActiveAction', 'updatePresentationAction', 'setSlideActiveAction']),
    async clearData() {
      if (this.loadingClearData) return

      this.loadingClearData = true

      const {
        id: presentationId, uniqueAccessCode, teamPlay,
      }
        = this.presentation

      const payload = {
        id: presentationId,
        socketId: Socket.getSocketId(),
      }
      if (teamPlay) {
        payload.uniqueAccessCode = uniqueAccessCode
        payload.teamPlay = teamPlay
      }
      try {
        await this.$http.post(Config.api.clearAudienceData, payload)
        this.patchPresentationAction({
          hasResults: false,
          version: this.presentation.version + 1,
        })
        this.$store.state.slideActive.countBackup = 0
      } catch (error) {
        const errorMsg = JSON.stringify(error)
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Results menu > Clear Audience data > Failed', errorMsg)

        throw error
      } finally {
        this.loadingClearData = false
        this.isClearDataConfirmationModalShowing = false
        this.patchPresentationAction({
          submitSlideLookup: null,
        })
      }

      this.clearPresentationReportAction()

      this.handleResetSlide()

      this.$store.state.audiences = {}
      this.$store.state.countPlayer = 0

      const index = this.presentation.slides.findIndex((item) => {
        return item.id === this.slideActive.id
      })

      this.updateSlideActiveAction({ id: null })
      setTimeout(() => {
        this.setSlideActiveAction(
          JSON.parse(JSON.stringify(this.presentation.slides[index])),
        )
      }, 0)
      this.$store.state.questions = []
      this.$store.state.audiences = {}
      this.$store.state.tabQnaQuestion = 1
      this.$store.state.lastQuestion = {}
      this.$store.state.questionCount = 0
      this.$store.state.countTopQuestion = 0
      this.$store.state.countPinnedQuestion = 0
      this.$store.state.countAnswerQuestion = 0

      this.updatePresentationAction({
        showExport: false,
        numberOfHearts: 0,
        numberOfLaughs: 0,
        numberOfLikes: 0,
        numberOfSads: 0,
        numberOfWows: 0,
        exportData: {
          reportUrl: null,
          reportStatus: null,
          reportUpdatedAt: null,
        },
      })

      if (!this.presentation.hasExampleResponses) {
        Utils.showToastSuccess(this.$i18nPresenter.t('Reset all results'))
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, `${this.gaAction} > Clear Audience data > Success`)
      } else {
        Utils.showToastSuccess(this.$i18nPresenter.t('Reset all results'))
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, `${this.gaAction} > Clear Example data > Success`)
      }

      if (this.presentation.teamData) {
        this.presentation.teamData.forEach((team) => {
          team.teams = []
        })
      }
    },
    handleResetSlide() {
      this.presentation.slides.forEach((slide) => {
        slide.version += 1
        if (slide.type === 'openEnded' || slide.type === 'ideas') {
          slide.SlideOptions = []
          slide.openEndedAIGroupedAnswers = []
          slide.isOpenEndedGroupingLayout = false
          slide.isOpenEndedGroupingLoading = false
        }
        if (slide.type === 'wordCloud') {
          slide.SlideOptions = []
          slide.wordCloudSmartGrouping = null
          slide.isGroupWordCloudWords = false
          slide.numberOfWordsInGroup = 0
          slide.numberOfUniqueWordsInGroup = 0
        }
        if (slide.type === 'spinnerWheel') {
          EventBus.$emit('clearSpinnerWheel', slide.id)
          slide.metadata = {
            ...slide.metadata,
            winner: null,
            entries: null,
            entryUrl: null,
            wheelStatus: null,
          }
        }

        if (slide.type === 'multipleChoice' || slide.type === 'pickAnswer' || slide.type === 'imageChoice') {
          slide.SlideOptions.forEach((slideOption) => {
            slideOption.count = 0
          })
        }
        slide.multipleCountAnswer = 0
        slide.votingStep = constants.IdeasTabName.SUBMISSION
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.clear-response-bigger {
  height: 36px;
}
</style>
